import React, { useCallback, useState } from 'react';
import { useExclusivePlayback } from './use-exclusive-playback';
import { VideoPlayerMode } from './use-video-playback';

export interface UseVideoMediaEventsParams {
  mode: VideoPlayerMode;
  loop?: boolean;
  playerRef: React.RefObject<HTMLVideoElement>;
  setPlaying: (playing: boolean) => void;
  onPlayingChange?: (playing: boolean) => void;
  onEnded?: () => void;
}

/** react-player v2 forwards `(error, data?)`: a DOM event, a rejected promise, or a provider payload. */
export type VideoErrorHandler = (error?: unknown, data?: unknown) => void;

export interface VideoMediaEvents {
  hasStarted: boolean;
  hasFatalError: boolean;
  onPlay: () => void;
  onPause: () => void;
  onStart: () => void;
  onEnded: () => void;
  onError: VideoErrorHandler;
}

interface ProviderErrorData {
  fatal: boolean;
}

/** hls.js, dash.js and flv.js all report their errors with an explicit `fatal` flag. */
function isProviderErrorData(data: unknown): data is ProviderErrorData {
  return typeof data === 'object' && data !== null && 'fatal' in data;
}

function getEventTarget(error: unknown): unknown {
  return typeof error === 'object' && error !== null && 'target' in error ? error.target : null;
}

function usePlaybackReporter(
  setPlaying: (playing: boolean) => void,
  onPlayingChange?: (playing: boolean) => void,
): (playing: boolean) => void {
  return useCallback(
    (nextPlaying: boolean) => {
      setPlaying(nextPlaying);
      onPlayingChange?.(nextPlaying);
    },
    [setPlaying, onPlayingChange],
  );
}

/**
 * Only a real media failure is fatal: a streaming library saying so itself, or a `MediaError` on
 * the element. The element is read from the event so a missing playlist or an unsupported codec
 * counts even before react-player reported the source as ready. Everything else — a `play()`
 * promise rejected by a quick pause, a recoverable segment retry — must not tear the player down.
 */
function isFatalError(error: unknown, data: unknown, player: HTMLVideoElement | null): boolean {
  if (isProviderErrorData(data)) {
    return data.fatal;
  }

  const target = getEventTarget(error);
  if (target instanceof HTMLMediaElement) {
    return target.error !== null;
  }

  return Boolean(player?.error);
}

/**
 * Translates the media element callbacks into component state. Playback events are the only
 * reliable source of truth once a provider (YouTube, Vimeo) renders its own controls, and the
 * ambient hero background video is the one mode excluded from the page-wide "one at a time" rule.
 */
export function useVideoMediaEvents({
  mode,
  loop,
  playerRef,
  setPlaying,
  onPlayingChange,
  onEnded,
}: UseVideoMediaEventsParams): VideoMediaEvents {
  const [hasStarted, setHasStarted] = useState(false);
  const [hasFatalError, setHasFatalError] = useState(false);
  const reportPlaying = usePlaybackReporter(setPlaying, onPlayingChange);
  const stopPlayback = useCallback(() => setPlaying(false), [setPlaying]);
  const { claim } = useExclusivePlayback(stopPlayback, mode !== 'background');

  // The ambient background video is registered with playback exclusivity disabled, so its claim
  // is a no-op and no other player can pause it. react-player fires `onStart` once per instance,
  // so a replay after the video ended has to hide the poster again from here.
  const onPlay = useCallback(() => {
    claim();
    setHasStarted(true);
    reportPlaying(true);
  }, [claim, reportPlaying]);

  const handleEnded = useCallback(() => {
    reportPlaying(false);
    if (!loop) {
      setHasStarted(false);
    }

    onEnded?.();
  }, [reportPlaying, loop, onEnded]);

  const onError = useCallback<VideoErrorHandler>(
    (error, data) => {
      if (!isFatalError(error, data, playerRef.current)) {
        return;
      }

      setHasFatalError(true);
      setHasStarted(false);
      reportPlaying(false);
    },
    [playerRef, reportPlaying],
  );

  return {
    hasStarted,
    hasFatalError,
    onPlay,
    onPause: useCallback(() => reportPlaying(false), [reportPlaying]),
    onStart: useCallback(() => setHasStarted(true), []),
    onEnded: handleEnded,
    onError,
  };
}
